import { sitesApi } from "#/api/sitesApi";
import ArchivedBadge from "#/components/ui/ArchivedBadge";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import DonutChart from "#/components/ui/DonutChart";
import InventoryMap from "#/components/ui/InventoryMap";
import type { CreateSitePayload, Site } from "#/types/sites";
import { Accordion, Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconExternalLink } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useTranslation } from "react-i18next";
import { logger } from "#/utils/logger";
import { getHardwareLabel } from "#/utils/deviceHardware";

/** The editable part of a site, as the form holds it. */
const toFormValues = (site: Pick<Site, "name" | "location_text">): CreateSitePayload => ({
  name: site.name ?? "",
  location_text: site.location_text ?? "",
});

const SiteDetailPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { canWrite } = usePermission();
  const { t } = useTranslation();

  const form = useForm<CreateSitePayload>({
    initialValues: {
      name: "",
      location_text: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name) errors.name = t("sites.validation.nameRequired");
      if (!values.location_text) errors.location_text = t("sites.validation.locationRequired");
      return errors;
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    siteMutation.mutate(values);
  };

  const siteMutation = useMutation({
    mutationFn: async (payload: CreateSitePayload) => {
      if (id !== "new") {
        return await sitesApi.update(Number(id), payload);
      } else {
        return await sitesApi.create(payload);
      }
    },
    onSuccess: (data) => {
      if (id === "new") {
        void navigate(`/sites/${data.data.id}`);
      } else {
        // The form now matches what is stored: make that its clean baseline and
        // keep the cached site in step, so Save disables and a later refetch
        // has nothing to overwrite.
        const saved = toFormValues(data.data);
        form.setInitialValues(saved);
        form.resetDirty(saved);
        queryClient.setQueryData<Site>(["siteDetails", id], (current) =>
          current ? { ...current, ...data.data } : data.data,
        );
      }
      showSuccessNotification({
        message: id === "new" ? t("sites.siteCreatedSuccess") : t("sites.siteRegisteredSuccess"),
      });
    },
    onError: (error) => {
      logger.error("SiteDetail save error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const { data: site } = useQuery({
    queryKey: ["siteDetails", id],
    enabled: !!id && id !== "new",
    queryFn: async () => {
      if (!id || id === "new") throw new Error("Invalid siteId");

      const response = await sitesApi.getById(Number(id));
      if (!response.data) throw new Error("Failed to fetch site");
      return response.data;
    },
  });

  // Load the fetched site into the form, but never over unsaved edits: React
  // Query refetches on window focus, and that used to wipe what the user was
  // typing. Each fetched object is applied once; while the form is dirty it is
  // left alone and picked up after the next save makes the form clean again.
  const appliedSiteRef = useRef<Site | null>(null);
  useEffect(() => {
    if (!site || appliedSiteRef.current === site || form.isDirty()) return;
    const values = toFormValues(site);
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    appliedSiteRef.current = site;
  }, [site, form]);

  const isArchived = site?.archived === true;
  const isReadOnly = isArchived || !canWrite();

  return (
    <DetailPageLayout
      title={
        <div className="flex items-center gap-2">
          <span>{id != "new" ? t("sites.siteDetails") : t("sites.createSite")}</span>
          {isArchived && <ArchivedBadge />}
        </div>
      }
      navRightSection={
        <div className={`w-full flex justify-end gap-2 items-center`}>
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty() || isArchived}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={siteMutation.isPending}
              disabled={!form.isDirty() || !canWrite() || isArchived}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="w-full grid grid-cols-1 gap-4">
            <Accordion
              variant={"separated"}
              defaultValue={["basic", "analytics"]}
              multiple
            >
              <Accordion.Item value={"basic"}>
                <Accordion.Control>{t("common.basicDetails")}</Accordion.Control>
                <Accordion.Panel>
                  <div className="w-full grid grid-cols-[1fr_400px] gap-6">
                    <div className="w-full flex flex-col gap-4">
                      <TextInput
                        label={t("common.name")}
                        withAsterisk
                        placeholder={t("sites.enterSiteName")}
                        disabled={isReadOnly}
                        {...form.getInputProps("name")}
                      />

                      <TextInput
                        label={t("common.location")}
                        withAsterisk
                        placeholder={t("sites.enterSiteLocation")}
                        disabled={isReadOnly}
                        {...form.getInputProps("location_text")}
                      />
                    </div>

                    {id !== "new" && site && (
                      <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200">
                        <InventoryMap
                          markers={[
                            {
                              latitude: site.latitude,
                              longitude: site.longitude,
                              color: "#3b82f6",
                            },
                          ]}
                          zoom={15}
                        />
                      </div>
                    )}
                  </div>
                </Accordion.Panel>
              </Accordion.Item>

              {id !== "new" && site && (
                <Accordion.Item value={"analytics"}>
                  <Accordion.Control>{t("common.analytics")}</Accordion.Control>
                  <Accordion.Panel>
                    <div className="w-full grid grid-cols-2 gap-4">
                        {/* Left: Devices Card with Donut Chart - 50% */}
                        <div
                          className="border rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow"
                          style={{
                            borderColor: `var(--mantine-color-blue-3)`,
                            backgroundColor: `var(--mantine-color-blue-0)`
                          }}
                        >
                          <div className="mb-4 flex justify-between items-start">
                            <div className="text-sm font-medium text-gray-700">{t("dashboard.devices")}</div>
                            <div className="text-3xl font-extrabold" style={{ color: `var(--mantine-color-blue-7)` }}>
                              {site.total_devices || 0}
                            </div>
                          </div>
                          <div className="w-full items-center grid grid-cols-[100px_1fr] gap-4 mt-4">
                            <div className="w-full h-28">
                              <DonutChart
                                data={(() => {
                                  const allTypes = ['sgs_rpi', 'chainway_c72', 'chainway_u300'];
                                  const colors = ['#3b82f6', '#06b6d4', '#8b5cf6'];
                                  return allTypes.map((type, index) => ({
                                    name: getHardwareLabel(type, type),
                                    value: site.devices_by_type?.[type] || 0,
                                    color: colors[index],
                                  })).filter(item => item.value > 0);
                                })()}
                                size={100}
                                thickness={20}
                                withTooltip={false}
                              />
                            </div>
                            <div className="w-full h-fit flex flex-col gap-2">
                              {(() => {
                                const allTypes = ['sgs_rpi', 'chainway_c72', 'chainway_u300'];
                                const colors = ['#3b82f6', '#06b6d4', '#8b5cf6'];
                                return allTypes.map((type, index) => (
                                  <div
                                    key={type}
                                    className="w-full flex items-center justify-between px-4 py-2 bg-white rounded-lg"
                                  >
                                    <div className="flex items-center gap-4">
                                      <div
                                        className="w-4 h-4 rounded-full"
                                        style={{ background: colors[index] }}
                                      ></div>
                                      <span className="text-sm font-medium">
                                        {getHardwareLabel(type, type)}
                                      </span>
                                    </div>
                                    <span>{site.devices_by_type?.[type] || 0}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Right: Items and Products stacked - 50% */}
                        <div className="flex flex-col gap-4">
                          {/* Items Card */}
                          <div
                            className="border rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow flex-1 flex flex-col justify-between"
                            style={{
                              borderColor: `var(--mantine-color-green-3)`,
                              backgroundColor: `var(--mantine-color-green-0)`
                            }}
                            onClick={() => window.open(`/inventory/items?siteId=${site.id}`, '_blank')}
                          >
                            <div className="flex justify-between items-start">
                              <div className="text-3xl font-extrabold" style={{ color: `var(--mantine-color-green-7)` }}>
                                {site.total_items || 0}
                              </div>
                              <IconExternalLink size={16} className="opacity-60" />
                            </div>
                            <div className="text-sm font-medium text-gray-700 mt-auto">{t("dashboard.items")}</div>
                          </div>

                          {/* Categories Card */}
                          <div
                            className="border rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow flex-1 flex flex-col justify-between"
                            style={{
                              borderColor: `var(--mantine-color-orange-3)`,
                              backgroundColor: `var(--mantine-color-orange-0)`
                            }}
                            onClick={() => window.open(`/inventory/categories`, '_blank')}
                          >
                            <div className="flex justify-between items-start">
                              <div className="text-3xl font-extrabold" style={{ color: `var(--mantine-color-orange-7)` }}>
                                {site.total_products || 0}
                              </div>
                              <IconExternalLink size={16} className="opacity-60" />
                            </div>
                            <div className="text-sm font-medium text-gray-700 mt-auto">{t("dashboard.categories")}</div>
                          </div>
                        </div>
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
              )}
            </Accordion>
          </div>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default SiteDetailPage;
